import { useEffect, useState } from 'react';
import { apiFetch } from '../../auth/apiFetch';
import { getDeviceToken } from '../../auth/deviceToken';
import { ScopeProvider } from '../../hooks/useScope';
import { KioskEnroll } from './KioskEnroll';
import { LockOverlay } from './LockOverlay';
import type { LockVariant } from './LockOverlay';
import { registerKioskServiceWorker } from './serviceWorker';
import { useKioskAutoUpdate } from './useKioskAutoUpdate';
import { AnyListWidget } from './widgets/AnyListWidget';
import { BillsWidget } from './widgets/BillsWidget';
import { ClockWidget } from './widgets/ClockWidget';
import { EcobeeWidget } from './widgets/EcobeeWidget';
import { MediaPanel } from './widgets/MediaWidget';
import { RingWidget } from './widgets/RingWidget';
import { SecurityWidget } from './widgets/SecurityWidget';
import { UpcomingEventsWidget } from './widgets/UpcomingEventsWidget';
import { WeatherWidget } from './widgets/WeatherWidget';

// Root for the kiosk SPA. Three states:
//   1. No device token → enrollment screen
//   2. Token + locked  → lock overlay
//   3. Token + active  → widget grid (Phase 3 will fill this with widgets)
//
// State is polled from /api/kiosk/state every STATE_POLL_MS. The poll uses
// apiFetch, which is already kiosk-aware and will clear the device token on
// a 401 (e.g. revocation from Settings) and reload into the enrollment screen.

const STATE_POLL_MS = 5000;

interface KioskState {
  isLocked: boolean;
  lockedAt: string | null;
  lockVariant?: LockVariant;
}

function useKioskState(): { state: KioskState | null; offline: boolean } {
  const [state, setState] = useState<KioskState | null>(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let alive = true;

    async function tick() {
      try {
        const res = await apiFetch('/api/kiosk/state');
        if (!alive) return;
        if (res.ok) {
          const json = (await res.json()) as KioskState;
          setState(json);
          setOffline(false);
        } else {
          // apiFetch handles 401 by clearing + reloading; any other non-OK
          // status just marks us offline-ish so the UI can surface it.
          setOffline(true);
        }
      } catch {
        if (alive) setOffline(true);
      }
    }

    void tick();
    const t = setInterval(tick, STATE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return { state, offline };
}

export function KioskShell() {
  useEffect(() => {
    registerKioskServiceWorker();
  }, []);

  // The kiosk SPA owns its own browser-tab title (the app default is "Budget").
  useEffect(() => {
    const prev = document.title;
    document.title = 'Kiosk';
    return () => {
      document.title = prev;
    };
  }, []);

  // Reload the kiosk on its own when a new build is deployed.
  useKioskAutoUpdate();

  // In local dev we skip enrollment entirely: with no token, apiFetch sends no
  // Authorization header and the API's KIOSK_DEV_BYPASS fallback authenticates
  // the request as the kiosk device. In production an unenrolled kiosk still
  // shows the enrollment screen.
  const token = getDeviceToken();
  if (!token && !import.meta.env.DEV) return <KioskEnroll />;

  // ScopeProvider gates the MonthCalendar's useScope() call. The kiosk
  // has no scope-picker UI, so we just default to 'household' (the value
  // ScopeProvider reads from localStorage, which is unset on the Pi).
  return (
    <ScopeProvider>
      <KioskDashboard />
    </ScopeProvider>
  );
}

function KioskDashboard() {
  const { state, offline } = useKioskState();

  // DEV-only preview escape hatch. The local KIOSK_DEV_BYPASS device (id 0)
  // has no DB row and is hardcoded unlocked, so remote locks from Settings
  // never reach it — there's no way to see a lock screen locally otherwise.
  // Visit /kiosk?lock=showings (or ?lock=petcare / ?lock=clock) to force-render
  // a variant.
  if (import.meta.env.DEV) {
    const forced = new URLSearchParams(window.location.search).get('lock');
    if (forced === 'clock' || forced === 'petcare' || forced === 'showings') {
      return <LockOverlay variant={forced} />;
    }
  }

  if (state?.isLocked) {
    // Pass the variant straight through — LockOverlay maps any unknown value
    // back to the clock screen itself, so no client-side allowlist is needed
    // (and the old petcare-only ternary silently dropped newer variants).
    return <LockOverlay variant={state.lockVariant} />;
  }

  return (
    // h-screen + overflow-hidden anchors the kiosk to the viewport so
    // widgets can never push the page taller and trigger a scroll. Inside
    // the flex column, main uses min-h-0 + flex-1 so it actually shrinks
    // to the remaining space (flex children default to min-content height,
    // which would otherwise leak past the viewport).
    <div className="kiosk-shell h-screen overflow-hidden bg-canvas text-fg flex flex-col gap-6 p-6">
      {offline && (
        <header className="flex justify-end">
          <span className="text-xs uppercase tracking-widest text-fg-tertiary">
            Offline
          </span>
        </header>
      )}
      {/* 18-column grid tuned to the kitchen kiosk's 1080×1920 portrait
          display. Rows:
            1 — 360px: clock + media launchers | thermostat | weather
            2 — auto: security (left third) | cameras (height driven by the
                cameras' 16:9 player + thumbnail row)
            3 — 400px: AnyList (11) | Upcoming (7)
            4 — minmax(0,1fr): the Bills calendar absorbs whatever is left */}
      <main className="flex-1 min-h-0 grid grid-cols-[repeat(18,minmax(0,1fr))] gap-6 grid-rows-[360px_auto_400px_minmax(0,1fr)]">
        {/* Clock on top, music panel on the bottom — both inside a single
            col-span-6 slot so the Thermostat (next column) stays one
            full-height card. Outer flex-col gives the panel its content
            height (flex-none) and lets the Clock card absorb the remaining
            track (flex-1 min-h-0), so the clock shrinks to make room. The
            MediaPanel shows the 2-up YouTube / Spotify launcher buttons when
            nothing is playing, and swaps to a compact now-playing card (with
            an X to stop) once a Music Assistant launch begins. */}
        <div className="col-span-6 flex flex-col gap-6">
          <ClockWidget className="flex-1 min-h-0" />
          <MediaPanel className="flex-none" />
        </div>
        <EcobeeWidget className="col-span-6" />
        <WeatherWidget className="col-span-6" />
        {/* Row 2 — the security house map (left third) + cameras (vertical
            player-over-thumbnails layout; see RingWidget). The
            LightsWidget that once sat
            beside the cameras is temporarily retired (2026-09, post-move);
            the widget file is kept (widgets/LightsWidget.tsx) and will return
            in a new manifestation. RingWidget re-enabled 2026-06-05 after the
            Pi 4 migration: the built-in Ring integration now serves
            idle-camera snapshots via camera_proxy, so the planned ring-mqtt
            add-on turned out to be unnecessary — the runbook at the top of
            RingWidget.tsx is left in place as history in case Amazon ever
            breaks the built-in path. */}
        <SecurityWidget className="col-span-6" />
        <RingWidget className="col-span-12" />
        <AnyListWidget className="col-span-11" />
        <UpcomingEventsWidget className="col-span-7" />
        <div className="col-span-full h-full">
          <BillsWidget />
        </div>
      </main>
    </div>
  );
}
